'use client'

import { useState } from 'react'

const sf = "var(--font-saira), 'Saira', sans-serif"
const GREEN = '#22c55e'
const MUTED = '#a1a1aa'

interface Tier {
  label: string
  volume: string
  threshold: number
  maker: number
  taker: number
}

interface Props {
  exchangeName: string
  makerFee: number
  takerFee: number
}

export default function VolumeTierCalculator({ exchangeName, makerFee, takerFee }: Props) {
  const [monthlyVol, setMonthlyVol] = useState(500_000)

  const tiers: Tier[] = [
    { label: 'Regular', volume: '< $1M',  threshold: 0,          maker: makerFee,                     taker: takerFee },
    { label: 'VIP 1',   volume: '$1M+',   threshold: 1_000_000,  maker: +(makerFee * 0.9).toFixed(3), taker: +(takerFee * 0.9).toFixed(3) },
    { label: 'VIP 2',   volume: '$5M+',   threshold: 5_000_000,  maker: +(makerFee * 0.8).toFixed(3), taker: +(takerFee * 0.8).toFixed(3) },
    { label: 'VIP 3',   volume: '$20M+',  threshold: 20_000_000, maker: +(makerFee * 0.7).toFixed(3), taker: +(takerFee * 0.7).toFixed(3) },
    { label: 'VIP 4',   volume: '$50M+',  threshold: 50_000_000, maker: +(makerFee * 0.6).toFixed(3), taker: +(takerFee * 0.6).toFixed(3) },
  ]

  const MAX_SLIDER = 60_000_000

  const currentTierIdx = [...tiers].map(t => t.threshold).filter(t => monthlyVol >= t).length - 1
  const nextTierIdx = Math.min(currentTierIdx + 1, tiers.length - 1)
  const currentTier = tiers[currentTierIdx]
  const nextTier = tiers[nextTierIdx]
  const nextThreshold = tiers[nextTierIdx]?.threshold ?? tiers[tiers.length - 1].threshold
  const isMaxTier = currentTierIdx === tiers.length - 1
  const progress = isMaxTier ? 100 : (monthlyVol / nextThreshold) * 100
  const remaining = Math.max(nextThreshold - monthlyVol, 0)

  const fmtVol = (v: number) =>
    v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${(v / 1_000).toFixed(0)}K`

  const fmtFee = (f: number) => f === 0 ? 'Free' : `${f.toFixed(3)}%`

  const monthlySavingsVsBase = ((makerFee - currentTier.taker) / 100) * monthlyVol
  const nextTierMonthlySavings = isMaxTier ? 0 : ((currentTier.taker - nextTier.taker) / 100) * monthlyVol

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-5">
      <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold font-[family-name:var(--font-saira)]">
          Volume discount tiers
        </h2>
        <span style={{ fontFamily: sf, fontSize: 10, color: '#71717a' }}>
          Estimated — verify on {exchangeName}'s official fee schedule
        </span>
      </div>

      {/* Slider */}
      <div style={{ marginBottom: 20 }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
    <label
      htmlFor="monthly-volume"
      style={{ fontFamily: sf, fontSize: 11, color: MUTED, cursor: 'pointer' }}
    >
      Your monthly trading volume
    </label>
    <span style={{ fontFamily: sf, fontSize: 14, fontWeight: 700, color: '#fff' }}>{fmtVol(monthlyVol)}</span>
  </div>
  <input
    id="monthly-volume"
    type="range"
    min={0}
    max={MAX_SLIDER}
    step={100_000}
    value={monthlyVol}
    onChange={e => setMonthlyVol(Number(e.target.value))}
    style={{ width: '100%', accentColor: GREEN, cursor: 'pointer', display: 'block' }}
  />
        {/* Tick labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          {tiers.map(t => (
            <span key={t.label} style={{ fontFamily: sf, fontSize: 8, color: '#71717a', textTransform: 'uppercase', letterSpacing: 1 }}>
              {t.threshold === 0 ? '$0' : fmtVol(t.threshold)}
            </span>
          ))}
        </div>
      </div>

      {/* Tier pills */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 20 }}>
        {tiers.map((tier, i) => {
          const isActive = i === currentTierIdx
          const isPast = i < currentTierIdx
          return (
            <div key={tier.label} style={{
              flex: 1, padding: '8px 6px', textAlign: 'center',
              background: isActive ? 'rgba(34,197,94,0.08)' : isPast ? '#050505' : '#0a0a0a',
              border: isActive ? '1px solid rgba(34,197,94,0.3)' : '1px solid #18181b',
              transition: 'all 0.2s',
            }}>
              <div style={{
                fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1,
                color: isActive ? GREEN : isPast ? '#3f3f46' : '#52525b',
                marginBottom: 3,
              }}>
                {tier.label}
              </div>
              <div style={{
                fontFamily: sf, fontSize: 12, fontWeight: 700,
                color: isActive ? '#fff' : isPast ? '#3f3f46' : '#27272a',
              }}>
                {fmtFee(tier.taker)}
              </div>
            </div>
          )
        })}
      </div>

      {/* Current tier summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 1,
        background: '#18181b',
        marginBottom: 12,
      }}>
        {[
          { label: 'Current tier',  value: currentTier.label },
          { label: 'Taker fee',     value: fmtFee(currentTier.taker), green: true },
          { label: 'Savings vs base', value: monthlySavingsVsBase > 0 ? `$${monthlySavingsVsBase.toFixed(0)}/mo` : '—', green: monthlySavingsVsBase > 0 },
        ].map(({ label, value, green }) => (
          <div key={label} style={{ background: '#000', padding: '12px 16px' }}>
            <div style={{ fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1.5, color: MUTED, marginBottom: 4 }}>{label}</div>
            <div style={{ fontFamily: sf, fontSize: 16, fontWeight: 800, color: green ? GREEN : '#fff' }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Next tier progress */}
      {!isMaxTier ? (
        <div style={{ padding: '16px 20px', background: '#050505', border: '1px solid #18181b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <span style={{ fontFamily: sf, fontSize: 12, fontWeight: 700, color: '#fff' }}>
              Next: {nextTier.label} — {fmtFee(nextTier.taker)} taker
            </span>
            <span style={{ fontFamily: sf, fontSize: 10, color: MUTED }}>{progress.toFixed(0)}% there</span>
          </div>

          {/* Progress bar */}
          <div style={{ height: 6, background: '#18181b', marginBottom: 10, position: 'relative' }}>
            <div style={{
              height: '100%',
              width: `${Math.min(progress, 100)}%`,
              background: 'linear-gradient(to right, #16a34a, #22c55e)',
              transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
            }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: sf, fontSize: 11, color: MUTED }}>
              Need{' '}
              <span style={{ color: '#fff', fontWeight: 700 }}>{fmtVol(remaining)}</span>
              {' '}more volume
            </span>
            {nextTierMonthlySavings > 0 && (
              <span style={{ fontFamily: sf, fontSize: 11, color: GREEN, fontWeight: 700 }}>
                Unlock → save ${nextTierMonthlySavings.toFixed(0)}/mo more
              </span>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          padding: '14px 20px',
          background: 'rgba(34,197,94,0.05)',
          border: '1px solid rgba(34,197,94,0.2)',
        }}>
          <span style={{ fontFamily: sf, fontSize: 13, fontWeight: 700, color: GREEN }}>
            Maximum tier — you're getting the best possible rate
          </span>
        </div>
      )}
    </div>
  )
}